import React, { useState } from 'react';
import { ArrowRight, Linkedin, Twitter, Globe } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernTeam — the «Moderno» theme "Our Team" (s139, approved by Anthony:
// T3 "Name Index"). Big serif names as an index with the role on the right;
// the pointed/focused name shows that person's portrait, bio and social links
// on the right. Phones: each row carries its thumbnail and bio.
// Data = aurex_team (config title/subtitle/eyebrow/max_visible/view_all_* +
// items name/role/photo_url/bio/linkedin_url/twitter_url/other_url).
// No photo → brand gradient with the initial. Colours from theme tokens.
// Portrait column = "Offset Frame" (approved s141).
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);

function Portrait({ m, className = '', style }) {
  const tt = useT();
  const src = img(m.photo_url);
  const [broken, setBroken] = useState(false);
  return src && !broken
    ? <img key={src} src={src} alt={tt(m.name)} onError={() => setBroken(true)} className={`object-cover ${className}`} style={style} />
    : <span className={`grid place-items-center text-white font-bold ${className}`} style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--color-accent, #ff9c1b) 55%, #fff), var(--color-accent, #ff9c1b))', fontFamily: SERIF, ...style }}>{(tt(m.name) || '?').charAt(0)}</span>;
}

function Socials({ m }) {
  const links = [[m.linkedin_url, Linkedin, 'LinkedIn'], [m.twitter_url, Twitter, 'X'], [m.other_url, Globe, 'Website']].filter(([u]) => u);
  if (!links.length) return null;
  return (
    <div className="flex gap-2">
      {links.map(([u, Icon, label]) => (
        <a key={label} href={u} target="_blank" rel="noopener noreferrer" aria-label={label} className="grid place-items-center w-9 h-9 rounded-full border transition-transform duration-150 active:scale-95"
          style={{ borderColor: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 40%, transparent)', color: 'var(--accent-strong, #b86500)' }}><Icon className="w-4 h-4" /></a>
      ))}
    </div>
  );
}

export default function ModernTeam({ config = {}, items = [] }) {
  const tt = useT();
  const { lang } = useLang();
  const all = (items || []).filter(i => i.visible !== false && itemHasLocale(i.name, lang));
  const [active, setActive] = useState(0);
  if (!all.length) return null;
  const limit = Number(config.max_visible) || all.length;
  const people = all.slice(0, limit);
  const cur = people[Math.min(active, people.length - 1)];

  const title = tt(config.title);
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  const last = words.length > 1 ? words.pop() : null;
  const viewAll = config.show_view_all && tt(config.view_all_text) && config.view_all_url;

  return (
    <section id="aurex-team" data-testid="aurex-section-team" className="bg-white">
      <style>{`.mtm-face{transition:opacity 450ms cubic-bezier(.23,1,.32,1)}@media (prefers-reduced-motion:reduce){.mtm-face{transition:none}}`}</style>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 lg:mb-14">
          <div className="flex flex-col gap-4 max-w-2xl">
            {tt(config.eyebrow) && (
              <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
                <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(config.eyebrow)}
              </div>
            )}
            {title && (
              <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)' }} data-testid="team-title">
                {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>}
              </h2>
            )}
            {tt(config.subtitle) && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(config.subtitle)}</p>}
          </div>
          {viewAll && (
            <a href={config.view_all_url} target={config.view_all_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start md:self-end" style={{ color: 'var(--accent-strong, #b86500)' }} data-testid="team-view-all">
              {tt(config.view_all_text)} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-12 lg:gap-16 items-start">
          <ul className="m-0 p-0 list-none border-t" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
            {people.map((m, i) => {
              const on = m === cur;
              return (
                <li key={m.id} className="border-b" style={{ borderColor: 'rgba(20,26,58,.1)' }} data-testid={`team-member-${m.id}`}>
                  <button type="button" onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onClick={() => setActive(i)}
                    className="w-full flex items-center justify-between gap-4 py-5 lg:py-6 text-left">
                    <span className="flex items-center gap-4 min-w-0">
                      <Portrait m={m} className="lg:hidden w-14 h-14 rounded-full flex-none" />
                      <span className="font-semibold truncate transition-colors duration-200" style={{ fontFamily: SERIF, fontSize: 'clamp(1.4rem, 2.6vw, 2.25rem)', lineHeight: 1.15, color: on ? 'var(--accent-strong, #b86500)' : 'var(--color-heading, #1a2332)' }}>{tt(m.name)}</span>
                    </span>
                    {tt(m.role) && <span className="flex-none text-[11px] font-bold uppercase text-right" style={{ letterSpacing: '.12em', color: 'var(--accent-strong, #b86500)' }}>{tt(m.role)}</span>}
                  </button>
                  {on && (tt(m.bio) || m.linkedin_url || m.twitter_url || m.other_url) && (
                    <div className="lg:hidden pb-5 flex flex-col gap-3">
                      {tt(m.bio) && <p className="text-sm leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(m.bio)}</p>}
                      <Socials m={m} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Approved "Offset Frame" (s141): portrait with an accent outline offset
              behind it, a graphite name plate over the lower-left corner, and the
              portraits cross-fading as the pointed name changes. */}
          <div className="hidden lg:flex flex-col gap-6 sticky top-28" data-testid="team-preview">
            <div className="relative pr-6 pb-6">
              <span aria-hidden="true" className="absolute top-6 left-6 right-0 bottom-0 rounded-[3px] border-[1.5px]" style={{ borderColor: 'var(--color-accent, #ff9c1b)' }} />
              <div className="relative aspect-[4/5] rounded-[3px] overflow-hidden" style={{ background: '#16171a' }}>
                {people.map(m => (
                  <Portrait key={m.id} m={m} className="mtm-face absolute inset-0 w-full h-full"
                    style={{ fontSize: '5rem', opacity: m === cur ? 1 : 0 }} />
                ))}
              </div>
              <div className="absolute -left-7 bottom-12 max-w-[80%] px-5 py-4 rounded-[3px] flex flex-col gap-1 text-white" style={{ background: '#16171a', boxShadow: '0 18px 40px -20px rgba(10,12,20,.6)' }} aria-live="polite">
                <span className="font-semibold" style={{ fontFamily: SERIF, fontSize: '1.3rem', lineHeight: 1.2 }}>{tt(cur.name)}</span>
                {tt(cur.role) && <span className="text-[10.5px] font-bold uppercase" style={{ letterSpacing: '.14em', color: 'var(--color-accent, #ff9c1b)' }}>{tt(cur.role)}</span>}
              </div>
            </div>
            {tt(cur.bio) && <p className="text-base leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(cur.bio)}</p>}
            <Socials m={cur} />
          </div>
        </div>
      </div>
    </section>
  );
}
